import { Component, DestroyRef, ElementRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormGroup,
  Validators,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
// Third Party
import {
  AccountingTypes,
  Answers,
  ComponentNames,
  InventoryTypes,
  ModalID,
  ModalType,
  Questions,
} from 'src/app/shared/models/constants';
// Services
import { SharedService } from 'src/app/shared/services/shared.service';
import { Account, ProductServiceItem } from '../../models/accounting';
import { DummyDataService } from '../../services/dummy-data/dummy-data.service';
import { numberOnlyValidator, usCurrencyValidator } from '../../functions';
import { saveProductServiceResponse } from '../../models/general';
import { AccountingService } from 'src/app/accounting/services/accounting.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-create-product-service',
  templateUrl: './create-product-service.component.html',
  styleUrls: ['./create-product-service.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule],
})
export class CreateProductServiceComponent {
  private destroyRef = inject(DestroyRef);
  subscriptions = new Subscription();
  product_service_form: FormGroup;
  item: ProductServiceItem;
  modal_type: string;
  row_index: number;
  calling_component: string;

  product_service: string;
  form_type: string;
  ngif_type: string = InventoryTypes.inventory;

  get isInventory(): boolean {
    const type = this.product_service_form?.get('type')?.value;
    return type === InventoryTypes.inventory || type === 'Product' || type === 'PRODUCT';
  }

  new_service_product = new ProductServiceItem();
  types: string[] = [InventoryTypes.inventory, InventoryTypes.service];
  income_accounts: Account[] = [];
  expense_accounts: Account[] = [];

  vendors = this.accountingService.vendors;
  products_services: ProductServiceItem;
  duplicate_enabled_entry = true;
  duplicate_entry = true;

  /** Friendly labels for the required form controls. The set varies between
   * Inventory and Service items — see computeMissingFields() for the runtime
   * filter. */
  private static readonly REQUIRED_FIELD_LABELS: Record<string, string> = {
    name: 'Name',
    description: 'Description',
    sale_price_rate: 'Sale Price Rate',
    cost: 'Cost',
    income_asset_account: 'Income Asset Account',
    income_account: 'Income Account',
    expense_account: 'Expense Account',
    inventory_asset_account: 'Inventory Asset Account',
    reorder_point: 'Reorder Point',
    sales_tax: 'Sales Tax',
  };

  /** Names of required fields that aren't yet satisfied. Empty array hides
   * the alert. Rendered as chips in the modal header. */
  missing_fields: string[] = [];

  constructor(
    protected element: ElementRef,
    private fb: FormBuilder,
    public sharedService: SharedService,
    public dummyDataService: DummyDataService,
    public accountingService: AccountingService,
  ) {
    // Initializing Form
    this.product_service_form = this.fb.group(
      {
        type: new FormControl({ value: null, disabled: false }, []),
        name: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        upc: new FormControl({ value: null, disabled: false }, []),
        category: new FormControl({ value: null, disabled: false }, []),
        description: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        sale_price_rate: new FormControl({ value: null, disabled: false }, [
          Validators.required,
          usCurrencyValidator(),
        ]),
        cost: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        income_asset_account: new FormControl(
          { value: null, disabled: false },
          [Validators.required],
        ),
        income_account: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        expense_account: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        inventory_asset_account: new FormControl(
          { value: null, disabled: false },
          [Validators.required],
        ),
        reorder_point: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        sales_tax: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        preferred_vendor: new FormControl({ value: null, disabled: false }, []),
        serial_item: new FormControl({ value: null, disabled: false }, []),
        active: new FormControl({ value: null, disabled: false }, []),
        margin_percentage: new FormControl({ value: null, disabled: false }, []),
      },
      // { validators: serialNumberMatchValidator }
    );
    // yes_no_submit$
    this.subscriptions.add(
      this.sharedService.yes_no_submit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (
            response.answer === Answers.yes &&
            response.question === Questions.close_create_product_service
          ) {
            this.closeNodal();
            this.sharedService.clear_bulk_acquisition_rows$.next(
              response.optional_details.row_index,
            );
            this.accountingService.product_service_modal_canceled$.next(
              response.optional_details.row_index,
            );
          } else if (
            response.answer === Answers.yes &&
            response.question === Questions.add_product_item_data
          ) {
            this.product_service_form.patchValue({
              name: response.optional_details.upc,
              description: response.optional_details.description,
              income_asset_account: 'Inventory Asset',
              income_account: 'Merchandise Sales',
              expense_account: 'Cost of Goods Sold',
              inventory_asset_account: 'Inventory Asset',
              reorder_point: 0,
            });
          }
        }),
    );
  }
  ngOnInit(): void {
    this.accountingService.categories = [...this.accountingService.categories];
    console.log(this.accountingService.categories);
    if (this.modal_type === ModalType.edit) {
      this.populateForm();
      if (this.item.type !== InventoryTypes.inventory && this.item.type !== 'Product' && this.item.type !== 'PRODUCT') {
        this.product_service_form.get('reorder_point').setValidators([]);
        this.product_service_form.get('reorder_point').updateValueAndValidity();
        this.product_service_form.get('cost').setValidators([]);
        this.product_service_form.get('cost').updateValueAndValidity();
        this.product_service_form.get('income_asset_account').setValidators([]);
        this.product_service_form
          .get('income_asset_account')
          .updateValueAndValidity();
        this.product_service_form
          .get('inventory_asset_account')
          .setValidators([]);
        this.product_service_form
          .get('inventory_asset_account')
          .updateValueAndValidity();
      }
    } else {
      if (this.item.type === InventoryTypes.inventory || this.item.type === 'Product' || this.item.type === 'PRODUCT') {
        this.product_service_form.patchValue({
          name: this.item.name,
          upc: this.item.upc,
          description: this.item.description,
          sale_price_rate: this.item.sale_price_rate,
          cost: this.item.cost,
          type: this.item.type ? this.item.type : InventoryTypes.inventory,
          sales_tax: this.item.sales_tax !== null ? this.item.sales_tax : true,
          active: true,
          serial_item: this.item.serial_item === true ? true : false,
          preferred_vendor: this.item.preferred_vendor
            ? this.item.preferred_vendor
            : null,
          reorder_point: this.item.reorder_point ? this.item.reorder_point : 0,
          income_asset_account: this.item.income_asset_account
            ? this.item.income_asset_account
            : null,
          income_account: this.item.income_account
            ? this.item.income_account
            : null,
          expense_account: this.item.expense_account
            ? this.item.expense_account
            : null,
          inventory_asset_account: this.item.inventory_asset_account
            ? this.item.inventory_asset_account
            : null,
        });
      } else {
        this.product_service_form.patchValue({
          name: this.item.name,
          upc: this.item.upc,
          description: this.item.description,
          sale_price_rate: this.item.sale_price_rate,
          type: this.item.type,
          sales_tax: false,
          active: true,
        });
        this.product_service_form.get('reorder_point').setValidators([]);
        this.product_service_form.get('reorder_point').updateValueAndValidity();
        this.product_service_form.get('cost').setValidators([]);
        this.product_service_form.get('cost').updateValueAndValidity();
        this.product_service_form.get('income_asset_account').setValidators([]);
        this.product_service_form
          .get('income_asset_account')
          .updateValueAndValidity();
        this.product_service_form
          .get('inventory_asset_account')
          .setValidators([]);
        this.product_service_form
          .get('inventory_asset_account')
          .updateValueAndValidity();
      }
    }
    this.getExpenseAccounts();
    this.getIncomeAccounts();

    this.product_service_form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshValidationMessage());

    // Bi-directional Margin Calculator
    this.product_service_form.get('margin_percentage').valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(margin => {
        if (this._isCalculatingMargin) return;
        const cost = parseFloat(this.product_service_form.get('cost')?.value);
        const m = parseFloat(margin);
        if (!isNaN(m) && !isNaN(cost) && cost > 0) {
          const newSalePrice = cost * (1 + (m / 100));
          this._isCalculatingMargin = true;
          this.product_service_form.patchValue({ sale_price_rate: newSalePrice.toFixed(2) });
          this._isCalculatingMargin = false;
        }
      });

    this.product_service_form.get('sale_price_rate').valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(salePrice => {
        if (this._isCalculatingMargin) return;
        const cost = parseFloat(this.product_service_form.get('cost')?.value);
        const sp = parseFloat(salePrice);
        if (!isNaN(sp) && !isNaN(cost) && cost > 0) {
          const newMargin = ((sp - cost) / cost) * 100;
          this._isCalculatingMargin = true;
          this.product_service_form.patchValue({ margin_percentage: newMargin.toFixed(2) });
          this._isCalculatingMargin = false;
        }
      });

    this.product_service_form.get('cost').valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(costStr => {
        if (this._isCalculatingMargin) return;
        const cost = parseFloat(costStr);
        const marginStr = this.product_service_form.get('margin_percentage')?.value;
        const margin = parseFloat(marginStr);
        // If cost changes and we have a margin set, update the sale price
        if (!isNaN(cost) && cost > 0 && !isNaN(margin)) {
          const newSalePrice = cost * (1 + (margin / 100));
          this._isCalculatingMargin = true;
          this.product_service_form.patchValue({ sale_price_rate: newSalePrice.toFixed(2) });
          this._isCalculatingMargin = false;
        }
      });

    // Dynamically update validators when the type changes
    this.product_service_form.get('type').valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isInventory) {
          this.product_service_form.get('reorder_point').setValidators([Validators.required]);
          this.product_service_form.get('cost').setValidators([Validators.required]);
          this.product_service_form.get('income_asset_account').setValidators([Validators.required]);
          this.product_service_form.get('inventory_asset_account').setValidators([Validators.required]);
        } else {
          this.product_service_form.get('reorder_point').setValidators([]);
          this.product_service_form.get('cost').setValidators([]);
          this.product_service_form.get('income_asset_account').setValidators([]);
          this.product_service_form.get('inventory_asset_account').setValidators([]);
        }
        
        this.product_service_form.get('reorder_point').updateValueAndValidity();
        this.product_service_form.get('cost').updateValueAndValidity();
        this.product_service_form.get('income_asset_account').updateValueAndValidity();
        this.product_service_form.get('inventory_asset_account').updateValueAndValidity();
      });
  }
  /** True when the Cost field is showing the weighted-average from open
   * inventory lots rather than the manually-entered cost. Drives the
   * "(avg from on-hand)" hint in the template. */
  cost_from_avg: boolean = false;

  populateForm() {
    // For Inventory items with stock on hand, prefer the weighted-average
    // cost computed by the COGS calculator over the manually-entered cost —
    // it reflects what the user actually paid for what they currently have.
    const useAvg =
      this.item.type === InventoryTypes.inventory &&
      typeof this.item.avg_cost === 'number' &&
      this.item.avg_cost > 0;
    const cost = useAvg ? this.item.avg_cost : this.item.cost;
    this.cost_from_avg = useAvg;

    this.product_service_form.patchValue({
      name: this.item.name,
      type: this.item.type,
      upc: this.item.upc,
      category: this.item.category,
      description: this.item.description,
      sale_price_rate: this.item.sale_price_rate,
      cost,
      income_asset_account: this.item.income_asset_account,
      income_account: this.item.income_account,
      expense_account: this.item.expense_account,
      inventory_asset_account: this.item.inventory_asset_account,
      reorder_point: this.item.reorder_point,
      sales_tax: this.item.sales_tax,
      preferred_vendor: this.item.preferred_vendor,
      serial_item: this.item.serial_item,
      active: this.item.active,
    });
  }
  getIncomeAccounts() {
    this.income_accounts = [
      ...this.accountingService.income_expense_accounts.filter(
        (record) => record.type === AccountingTypes.income,
      ),
    ];
  }
  getExpenseAccounts() {
    this.expense_accounts = [
      ...this.accountingService.income_expense_accounts.filter(
        (account) => account.type === AccountingTypes.expense,
      ),
    ];
  }
  closeModalClick(): void {
    if (this.calling_component === ComponentNames.bulk_acquisition) {
      const optional_details = { row_index: this.row_index };
      this.sharedService.openYesNoModal(
        Questions.close_create_product_service,
        optional_details,
      );
    } else {
      this.closeNodal();
      this.accountingService.product_service_modal_canceled$.next(true);
    }
  }
  closeNodal(): void {
    this.sharedService.closeModal(ModalID.add_edit_product_service);
  }
  cancelModalClick(): void {
    this.sharedService.closeModal(ModalID.add_edit_product_service);
    console.warn(this.row_index);
    this.accountingService.product_service_modal_canceled$.next(this.row_index);
  }
  checkDuplicateProductService(): boolean {
    const name = this.product_service_form.get('name').value;
    const upc = this.product_service_form.get('upc').value;
    const currentId = this.modal_type === ModalType.edit ? this.item.id : null;

    const existingItems = this.accountingService.products_services || [];
    
    for (const item of existingItems) {
      if (currentId && item.id === currentId) {
        continue;
      }
      if (name && item.name && item.name.trim().toLowerCase() === name.trim().toLowerCase()) {
        this.sharedService.raiseErrorToast(`An item with the name '${name}' already exists.`);
        return true;
      }
      if (upc && item.upc && item.upc.trim() === upc.trim()) {
        this.sharedService.raiseErrorToast(`An item with the UPC '${upc}' already exists.`);
        return true;
      }
    }
    return false;
  }

  /** Return the human-readable names of every required field that isn't
   * yet satisfied. Mirrors the runtime validator stripping done in ngOnInit
   * for Service items (which don't require Inventory-specific fields). */
  private computeMissingFields(): string[] {
    const missing: string[] = [];
    const isService =
      this.product_service_form.get('type')?.value === InventoryTypes.service;
    for (const [control, label] of Object.entries(
      CreateProductServiceComponent.REQUIRED_FIELD_LABELS,
    )) {
      // For Service items, these fields have their validators removed in
      // ngOnInit — keep the alert consistent with the actual Save gate.
      if (
        isService &&
        ['reorder_point', 'cost', 'income_asset_account', 'inventory_asset_account'].includes(
          control,
        )
      ) {
        continue;
      }
      if (this.product_service_form.get(control)?.invalid) missing.push(label);
    }
    return missing;
  }

  /** Re-evaluate the alert as the user edits. No-op until the user has
   * clicked Save at least once. */
  refreshValidationMessage(): void {
    if (this.missing_fields.length === 0) return;
    this.missing_fields = this.computeMissingFields();
  }

  /** Click handler for the Save button. The button is visually disabled
   * while the form is invalid but remains clickable so we can tell the
   * user which required field is missing — mirrors the Sales receipt UX. */
  attemptSave(): void {
    const missing = this.computeMissingFields();
    if (missing.length > 0) {
      this.product_service_form.markAllAsTouched();
      this.missing_fields = missing;
      return;
    }
    this.missing_fields = [];
    this.saveProductServiceClick();
  }

  saveProductServiceClick() {
    if (this.checkDuplicateProductService()) {
      return;
    }

    const product_service_item: ProductServiceItem = new ProductServiceItem();
    if (this.modal_type === ModalType.edit) {
      product_service_item.id = this.item.id;
    } else {
      product_service_item.id = null;
    }
    product_service_item.type = this.product_service_form.get('type').value;
    product_service_item.name = this.product_service_form.get('name').value;
    product_service_item.upc = this.product_service_form.get('upc').value;
    product_service_item.category =
      this.product_service_form.get('category').value;
    product_service_item.description =
      this.product_service_form.get('description').value;
    product_service_item.serial_item =
      this.product_service_form.get('serial_item').value;
    product_service_item.sale_price_rate =
      this.product_service_form.get('sale_price_rate').value;
    product_service_item.cost = this.product_service_form.get('cost').value;
    product_service_item.income_asset_account = this.product_service_form.get(
      'income_asset_account',
    ).value;
    product_service_item.income_account =
      this.product_service_form.get('income_account').value;
    product_service_item.expense_account =
      this.product_service_form.get('expense_account').value;
    product_service_item.inventory_asset_account =
      this.product_service_form.get('inventory_asset_account').value;
    product_service_item.reorder_point =
      this.product_service_form.get('reorder_point').value;
    product_service_item.sales_tax =
      this.product_service_form.get('sales_tax').value;
    product_service_item.preferred_vendor =
      this.product_service_form.get('preferred_vendor').value;
    product_service_item.active = this.product_service_form.get('active').value;
    product_service_item.archived = false;

    this.sharedService.closeModal(ModalID.add_edit_product_service);
    let returned_response = new saveProductServiceResponse();
    returned_response.rowIndex = this.row_index;
    returned_response.item = product_service_item;
    if (product_service_item.id === null) {
      this.accountingService.product_service_to_add$.next(returned_response);
    } else {
      this.accountingService.product_service_to_edit$.next(returned_response);
    }
  }
  checkForDecimal(event: any) {
    const controlName =
      event.target.getAttribute('formControlName') ||
      event.target.id ||
      event.target.name;
    const control = this.product_service_form.get(controlName);

    if (
      !control ||
      control.value === null ||
      control.value === undefined ||
      control.value === ''
    ) {
      return;
    }

    const controlValue = control.value.toString();

    if (!controlValue.includes('.')) {
      const newValue = `${controlValue}.00`;
      this.product_service_form.patchValue({ [controlName]: newValue });
    } else {
      const parts = controlValue.split('.');
      if (parts[1].length < 2) {
        const newValue = `${controlValue}0`;
        this.product_service_form.patchValue({ [controlName]: newValue });
      }
    }
  }
  
  private _isCalculatingMargin = false;

  findItemByUpc() {
    const upc = this.product_service_form.get('upc').value;
    if (!upc) {
      return;
    }
    const is_number = numberOnlyValidator(upc);
    if (is_number) {
      this.sharedService.openStatusSpinner('Checking Vendor Data...');
      this.sharedService.getVendorData(upc).subscribe({
        next: (live_inventory) => {
          this.sharedService.closeStatusSpinner();
          if (!live_inventory) {
            this.sharedService.raiseErrorToast(
              'No vendor data found for this item. Please check the UPC and try again.',
            );
          } else {
            const optional_details = {
              upc: live_inventory.upc,
              description: live_inventory.desc1,
              income_asset_account: 'Inventory Asset',
              income_account: 'Merchandise Sales',
              expense_account: 'Cost of Goods Sold',
              inventory_asset_account: 'Inventory Asset',
              reorder_point: 0,
            };
            this.sharedService.openYesNoModal(
              Questions.add_product_item_data,
              optional_details,
              true
            );
          }
        },
        error: (err) => {
          this.sharedService.closeStatusSpinner();
          this.sharedService.raiseErrorToast(
            'Error fetching vendor data: ' + err.message,
          );
        },
      });
    }
  }
  //   patchInfoClick() {
  //    const product_service_item =
  //        {
  //            name: '082442816371',
  //       upc: '082442816371',
  //       type: 'Inventory',
  //       category: null,
  //       description: 'BERETTA M9 9MM 4.9" 15RD BLK',
  //       serial_item: true,
  //       qty: 1,
  //       sale_price_rate: 799.99,
  //       cost: 699.99,
  //       income_asset_account: 'Inventory Asset',
  //       income_account: 'Merchandise Sales',
  //       expense_account: 'Cost of Goods Sold',
  //       inventory_asset_account: 'Inventory Asset',
  //       reorder_point: 1,
  //       sales_tax: true,
  //       preferred_vendor: 'RSR Group',
  //       active: false,
  //       archived: false,
  //     };
  // this.product_service_form.patchValue({
  //   name: product_service_item.name,
  //   type: product_service_item.type,
  //   upc: product_service_item.upc,
  //   category: product_service_item.category,
  //   description: product_service_item.description,
  //   qty: product_service_item.qty,
  //   sale_price_rate: product_service_item.sale_price_rate,
  //   cost: product_service_item.cost,
  //   income_asset_account: product_service_item.income_asset_account,
  //   income_account: product_service_item.income_account,
  //   expense_account: product_service_item.expense_account,
  //   inventory_asset_account: product_service_item.inventory_asset_account,
  //   reorder_point: product_service_item.reorder_point,
  //   sales_tax: product_service_item.sales_tax,
  //   preferred_vendor: product_service_item.preferred_vendor,
  //   serial_item: product_service_item.serial_item,
  //   active: product_service_item.active,
  // });
  //   }
}
